import React from 'react';
import { ActivityIndicator, Icon, IconButton, Tooltip } from '@kontentino/ui';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import InstagramAudioArtwork from '../InstagramAudioArtwork';
import { formatTrackDetails } from '../../utils/formatTrackDetails';
import { resolvePreviewSrc } from '../../utils/resolvePreviewSrc';
import {
  toggleAudioPreview,
  useAudioPreviewTrack,
} from '../../hooks/useAudioPreview';
import type { InstagramAudioTrack } from '../../types';

interface InstagramAudioTrackListItemProps {
  track: InstagramAudioTrack;
  isSelected: boolean;
  onToggleSelect: (track: InstagramAudioTrack) => void;
}

const InstagramAudioTrackListItem = ({
  track,
  isSelected,
  onToggleSelect,
}: InstagramAudioTrackListItemProps) => {
  const { t } = useTranslation();
  const { isPlaying, isBuffering, hasFailed } = useAudioPreviewTrack(
    track.audio_id,
  );

  const previewSrc = resolvePreviewSrc(track);
  const canPreview = !!previewSrc && !hasFailed;
  const isActive = isPlaying || isBuffering;

  const subtitle = formatTrackDetails(track);

  const handleRowActivate = () => {
    if (canPreview) toggleAudioPreview(track.audio_id, previewSrc);
  };

  const row = (
    <div
      role="button"
      tabIndex={0}
      onClick={handleRowActivate}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleRowActivate();
        }
      }}
      data-cy="instagram-audio_track-row"
      data-name="instagram-audio_track-row"
      className={clsx(
        'tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-p-2 tw-transition-colors',
        {
          'tw-bg-grayscale-5': isSelected,
          'hover:tw-bg-grayscale-5': !isSelected,
        },
      )}
    >
      <div className="tw-relative tw-h-10 tw-w-10 tw-shrink-0">
        <InstagramAudioArtwork
          src={track.cover_artwork_thumbnail_uri ?? track.profile_picture_url}
          alt={track.title ?? ''}
        />

        {canPreview && (
          <div
            className={clsx(
              'tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-black/40 tw-text-white tw-transition-opacity',
              {
                'tw-opacity-100': isActive,
                'tw-opacity-0 group-hover:tw-opacity-100': !isActive,
              },
            )}
          >
            {isBuffering ? (
              <ActivityIndicator />
            ) : (
              <Icon icon={isPlaying ? faPause : faPlay} size="sm" />
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        <span
          className={clsx('tw-truncate tw-text-sm tw-font-medium', {
            'tw-text-primary-100': isActive,
            'tw-text-grayscale-180': !isActive,
          })}
        >
          {track.title}
        </span>
        {subtitle && (
          <span className="tw-truncate tw-text-xs tw-text-grayscale-140">
            {subtitle}
          </span>
        )}
      </div>

      <IconButton
        variant={isSelected ? 'primary' : 'tertiary'}
        size="small"
        icon={<Icon icon={isSelected ? faCheck : faPlus} size="sm" />}
        onClick={(event) => {
          event.stopPropagation();
          onToggleSelect(track);
        }}
        aria-label={
          isSelected
            ? t('instagramAudio.removeAudio')
            : t('instagramAudio.addAudio')
        }
        data-cy="instagram-audio_track-toggle-button"
      />
    </div>
  );

  if (canPreview) return row;

  return (
    <Tooltip arrow content={t('instagramAudio.previewUnavailable')}>
      {row}
    </Tooltip>
  );
};

export default InstagramAudioTrackListItem;
